/**
 * Engagement REST client (T2.14 / D-25 — trips domain). Reactions are ON
 * trips, so these routes live on the TRIPS API (same base + auth as
 * services/trips): ownership/visibility checks happen where trips live.
 *
 *   PUT/DELETE {base}/trips/{id}/like       like / unlike
 *   PUT/DELETE {base}/trips/{id}/bookmark   save / unsave (private)
 *   GET        {base}/users/me/bookmarks    my saved trip ids
 *
 * There is deliberately NO batch reactions call: trip reads arrive already
 * enriched with `likes` + `viewer {liked, saved}` (one request per screen) —
 * see Trip in services/trips/api/tripsApi.ts and tripReaction() below.
 * Bookmarks are PRIVATE: no surface exposes save counts, so Reaction carries
 * only the public like count and the viewer's own state.
 */

import { getTokensFromCache } from '../auth/utils/tokenCache';
import type { Trip } from '../trips/api/tripsApi';

const TRIPS_API_ENDPOINT = process.env.REACT_APP_APIGATEWAY_TRIPS_ENDPOINT;

export interface Reaction {
  liked: boolean;
  saved: boolean;
  likes: number;
}

function base(): string {
  if (!TRIPS_API_ENDPOINT) {
    throw new Error('Trips API endpoint not configured (REACT_APP_APIGATEWAY_TRIPS_ENDPOINT)');
  }
  return TRIPS_API_ENDPOINT;
}

function authHeaders(): Record<string, string> {
  const token = getTokensFromCache()?.accessToken;
  if (!token) throw new Error('User not authenticated');
  return { Authorization: token }; // No Bearer prefix — API Gateway Cognito authorizer
}

const tripUrl = (tripId: string, suffix: string) =>
  `${base()}/trips/${encodeURIComponent(tripId)}${suffix}`;

async function toggle(method: 'PUT' | 'DELETE', tripId: string, kind: 'like' | 'bookmark'): Promise<void> {
  const response = await fetch(tripUrl(tripId, `/${kind}`), { method, headers: authHeaders() });
  if (!response.ok && response.status !== 204) {
    throw new Error(`Failed to ${method === 'PUT' ? 'add' : 'remove'} ${kind} (${response.status})`);
  }
}

export const likeTrip = (tripId: string) => toggle('PUT', tripId, 'like');
export const unlikeTrip = (tripId: string) => toggle('DELETE', tripId, 'like');
export const bookmarkTrip = (tripId: string) => toggle('PUT', tripId, 'bookmark');
export const unbookmarkTrip = (tripId: string) => toggle('DELETE', tripId, 'bookmark');

/** Reaction state carried on an enriched trip DTO (absent fields = zero state). */
export function tripReaction(trip: Pick<Trip, 'likes' | 'viewer'> | undefined): Reaction {
  return {
    likes: Math.max(0, Number(trip?.likes ?? 0)),
    liked: Boolean(trip?.viewer?.liked),
    saved: Boolean(trip?.viewer?.saved),
  };
}

export async function getMyBookmarks(cursor?: string): Promise<{ tripIds: string[]; cursor?: string }> {
  const suffix = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  const response = await fetch(`${base()}/users/me/bookmarks${suffix}`, { headers: authHeaders() });
  if (!response.ok) throw new Error(`Failed to load bookmarks (${response.status})`);
  const data = await response.json();
  return { tripIds: data.tripIds ?? [], cursor: data.cursor };
}

/** Honest-but-approximate display (D-25 §6): 1.2K, 34K, 1.1M. */
export function abbreviateCount(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0';
  if (n < 1000) return String(n);
  if (n < 1_000_000) {
    const k = n / 1000;
    return `${k >= 100 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, '')}K`;
  }
  const m = n / 1_000_000;
  return `${m >= 100 ? Math.round(m) : m.toFixed(1).replace(/\.0$/, '')}M`;
}
